import React from 'react'
import { Trans, withI18n } from '@lingui/react'
import { arrayOf, node, string, number, bool, shape } from 'prop-types'
import classNames from 'classnames'

import { i18nType } from 'views/utils/propTypes/i18nType'
import { CROSSSELL_ATTR_ANALYTICS_NAME } from 'shared/consts/crosssellWidgets'

import getTranslatedOrStringValue from 'views/utils/getTranslatedOrStringValue'

import Button from 'views/components/atoms/Button'
import ButtonNavigation from 'views/components/molecules/ButtonNavigation'
import SliderHeader from './SliderHeader'

import {
  VISIBLE_COUNT_TABLET_DEFAULT,
  VISIBLE_COUNT_MOBILE_DEFAULT,
  DEFAULT_NUMBER_OF_VISIBLE_ITEM,
  MORE_PRODUCTS_XSELL_ID,
  VISIBLE_COUNT_DESKTOP_DEFAULT
} from 'views/consts'

import './SliderHorizontal.scss'

export const renderItems = ({ items, colSize, noSlider }) =>
  items.map((item, index) => (
    <li
      className={classNames(
        'o-SliderHorizontal__item',
        'l-col--12-of-12',
        `l-col--${Math.ceil(colSize * 2)}-of-12@tiny`,
        `l-col--${Math.ceil(colSize)}-of-12@medium`,
        {
          'o-SliderHorizontal__item--no-slider l-flex l-flex--stretch-children': noSlider,
          'o-SliderHorizontal__item--only-item': items.length === 1
        }
      )}
      // eslint-disable-next-line react/no-array-index-key
      key={`slider-item-${index}`}
      data-clientside-hook="slider__item"
    >
      {item}
    </li>
  ))

class SliderHorizontal extends React.PureComponent {
  // eslint-disable-next-line complexity
  render() {
    const {
      items,
      visibleItemCount,
      visibleItemCountSmall,
      visibleItemCountTiny,
      title: titleRaw,
      moreLinkUrl,
      moreLinkTitle,
      wid,
      className,
      thumbnailNav,
      stretch,
      centered,
      adaptableArrows,
      adaptStrategy,
      noSlider,
      navWithOpacity,
      hideArrowsOnMobile,
      i18n,
      itemsForLazyLoad,
      noAutoFullWidth
    } = this.props

    if (!items || !items.length) {
      return null
    }

    const colCount =
      noSlider || items.length === 1
        ? 4
        : Math.min(items.length, visibleItemCount)

    const colSize = 12 / colCount
    const sliderClassName = classNames('o-SliderHorizontal', className, {
      'o-SliderHorizontal__nav-with-opacity': navWithOpacity
    })

    const title = getTranslatedOrStringValue(titleRaw, i18n)

    // TODO: we need to remove, adjust this depending on our future js solution
    // this is present for know, as it ensures correct rendering in the storybook
    // TODO: Also, let's remove all three versions of Sliders and pass down something
    // like a `type` property for deciding which title to show.

    const sliderProps = {
      className: sliderClassName,
      'data-crosssell-id': wid,
      'data-crosssell-title': title,
      'data-slider-adapt-strategy': adaptStrategy,
      'data-analytics': wid && CROSSSELL_ATTR_ANALYTICS_NAME
    }

    if (!noSlider) {
      sliderProps['data-clientside-hook'] = thumbnailNav
        ? 'slider__thumbnails'
        : 'slider'
      sliderProps['data-slider-slideby'] = visibleItemCount

      if (visibleItemCountSmall) {
        sliderProps['data-slider-slideby-small'] = visibleItemCountSmall
      }

      if (visibleItemCountTiny) {
        sliderProps['data-slider-slideby-tiny'] = visibleItemCountTiny
      }
    }

    const navigationClassName = additionalClassName =>
      classNames(
        `o-SliderHorizontal__nav u-transform-center--y ${additionalClassName}`,
        {
          'u-hidden@small-tiny': hideArrowsOnMobile,
          'o-SliderHorizontal__nav--hidden': adaptableArrows
        }
      )

    return (
      <div {...sliderProps}>
        {(title || moreLinkUrl) && (
          <SliderHeader {...{ title: titleRaw, moreLinkTitle, moreLinkUrl }} />
        )}
        {!noSlider && (
          <>
            <ButtonNavigation
              direction="left"
              iconSize="large"
              name="product-slicer-previous-button"
              data-clientside-hook={
                adaptableArrows
                  ? 'slider__prev adaptable__arrows'
                  : 'slider__prev'
              }
              isClientsideControlled
              className={navigationClassName('o-SliderHorizontal__previous')}
            >
              <Trans id="general.link.label.back" />
            </ButtonNavigation>
            <ButtonNavigation
              direction="right"
              iconSize="large"
              name="product-slider-next-button"
              data-clientside-hook={
                adaptableArrows
                  ? 'slider__next adaptable__arrows'
                  : 'slider__next'
              }
              isClientsideControlled
              className={navigationClassName('o-SliderHorizontal__next')}
            >
              <Trans id="general.link.label.forward" />
            </ButtonNavigation>
          </>
        )}
        <div
          className={classNames({
            'o-SliderHorizontal__wrapper': true,
            'o-SliderHorizontal__wrapper--noSlider': noSlider
          })}
        >
          <ul
            className={classNames(
              'o-SliderHorizontal__list a-list-reset u-position--relative',
              {
                'o-SliderHorizontal__list--stretch': stretch,
                'u-margin--auto': centered && !noSlider,
                'o-SliderHorizontal__list--no-slider': noSlider,
                'a-list-inline': !noSlider,
                'a-full-width':
                  !noAutoFullWidth &&
                  items.length < DEFAULT_NUMBER_OF_VISIBLE_ITEM
              }
            )}
            data-clientside-hook="slider__items"
          >
            {renderItems({ items, colSize, noSlider })}
          </ul>
          {itemsForLazyLoad && (
            <div
              data-clientside-hook={MORE_PRODUCTS_XSELL_ID}
              data-items={JSON.stringify(itemsForLazyLoad)}
            />
          )}
        </div>
        {itemsForLazyLoad && (
          <div className="o-SliderHorizontal__show-more u-text-align--center u-padding--top">
            <Button
              reverse
              inlineDesktopOnly
              data-clientside-hook="showMoreButton"
              isClientsideControlled
              name="show-more-products-button"
            >
              <Trans id="general.label.loadMoreProducts" />
            </Button>
          </div>
        )}
      </div>
    )
  }
}

SliderHorizontal.propTypes = {
  ...SliderHeader.propTypes,
  i18n: i18nType.isRequired,
  items: arrayOf(node),
  visibleItemCount: number,
  className: string,
  wid: number,
  thumbnailNav: bool,
  stretch: bool,
  centered: bool,
  adaptableArrows: bool,
  noSlider: bool,
  adaptStrategy: string,
  visibleItemCountSmall: number,
  navWithOpacity: bool,
  hideArrowsOnMobile: bool,
  noAutoFullWidth: bool,
  visibleItemCountTiny: number,
  itemsForLazyLoad: arrayOf(shape({}))
}

SliderHorizontal.defaultProps = {
  ...SliderHeader.defaultProps,
  items: null,
  className: null,
  wid: null,
  thumbnailNav: null,
  stretch: false,
  centered: true,
  adaptableArrows: false,
  noSlider: false,
  adaptStrategy: null,
  navWithOpacity: true,
  hideArrowsOnMobile: true,
  itemsForLazyLoad: null,
  noAutoFullWidth: false,
  visibleItemCount: VISIBLE_COUNT_DESKTOP_DEFAULT,
  visibleItemCountSmall: VISIBLE_COUNT_TABLET_DEFAULT,
  visibleItemCountTiny: VISIBLE_COUNT_MOBILE_DEFAULT
}

export default withI18n()(SliderHorizontal)
